if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.activeSwatch = null;

      this.disableSwipe = this.hasAttribute('data-disable-swipe');

      this.refresh();

      if (this.disableSwipe && this.mediaElement) {
        this.mediaElement.style.touchAction = 'auto';
      }

      this.addEventListener('intersection:loaded', () => this.bindSwatches());

      this.addEventListener('click', (e) => this._onClick(e));
      this.addEventListener('mouseover', (e) => this._onSwatchHover(e));
      this.addEventListener('focusin', (e) => this._onSwatchHover(e));

      if (!this.disableSwipe) this.setupSwipe();
    }

    refresh() {
      this.images = Array.from(this.querySelectorAll('img:not([aria-hidden])'));
      this.paginationInner = this.querySelector('[data-pagination-inner]');
      this.mediaElement = this.querySelector('[data-product-card-media]');
      this.mediaLink = this.querySelector('[data-product-card-link]');
      this.titleLink = this.querySelector('[data-tile-link]');
      this.priceElement = this.querySelector('[data-tile-price]');
      this.badgeContainer = this.querySelector('[data-badge-container]');
      this.ownHandle = this.getAttribute('data-handle');
      this.bindSwatches();
    }

    _onClick(e) {
      if (this._suppressNextClick) {
        this._suppressNextClick = false;
        e.preventDefault();
        return;
      }

      const swatch = e.target.closest('[data-cl-swatch]');
      if (swatch) {
        if (!swatch.classList.contains('cl-swatch--soldout')) this.commitSwatch(swatch);
        return;
      }

      const actionElement = e.target.closest('[data-action]');
      if (!actionElement) return;
      switch (actionElement.dataset.action) {
        case 'next-image':
          this.showImage(this.getCurrentImageIndex() + 1);
          break;
        case 'previous-image':
          this.showImage(this.getCurrentImageIndex() - 1);
          break;
        default:
          break;
      }
    }

    _onSwatchHover(e) {
      const swatch = e.target.closest('[data-cl-swatch]');
      if (swatch && !swatch.classList.contains('cl-swatch--soldout')) this.commitSwatch(swatch);
    }

    setupSwipe() {
      if (!this.mediaElement) return;

      const dragThreshold = 40;
      const axisLockThreshold = 10;
      let startX = 0;
      let startY = 0;
      let isDragging = false;
      let dragAxis = null;

      this.mediaElement.addEventListener('pointerdown', (e) => {
        if (e.target.closest('[data-action]')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        startX = e.clientX;
        startY = e.clientY;
        isDragging = true;
        dragAxis = null;
      });

      this.mediaElement.addEventListener('pointermove', (e) => {
        if (!isDragging) return;
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        if (!dragAxis) {
          if (Math.abs(deltaX) < axisLockThreshold && Math.abs(deltaY) < axisLockThreshold) return;
          dragAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        }
        if (dragAxis === 'x') e.preventDefault();
      });

      const endDrag = (e) => {
        if (!isDragging) return;
        if (dragAxis === 'x') {
          const deltaX = e.clientX - startX;
          if (Math.abs(deltaX) >= dragThreshold) {
            this._suppressNextClick = true;
            this.showImage(this.getCurrentImageIndex() + (deltaX < 0 ? 1 : -1));
          }
        }
        isDragging = false;
        dragAxis = null;
      };

      this.mediaElement.addEventListener('pointerup', endDrag);
      this.mediaElement.addEventListener('pointercancel', endDrag);
    }

    getCurrentImageIndex() {
      return this.images.findIndex((image) => image.classList.contains('sp-opacity-100'));
    }

    showImage(index) {
      const currentIndex = this.getCurrentImageIndex();
      if (currentIndex === -1 || this.images.length < 2) return;

      const nextIndex = (index + this.images.length) % this.images.length;
      if (nextIndex === currentIndex) return;

      this.images[currentIndex].classList.replace('sp-opacity-100', 'sp-opacity-0');
      this.images[nextIndex].classList.replace('sp-opacity-0', 'sp-opacity-100');

      if (this.paginationInner) {
        this.paginationInner.style.width = `${((nextIndex + 1) / this.images.length) * 100}%`;
      }
    }

    bindSwatches() {
      const swatches = Array.from(this.querySelectorAll('[data-cl-swatch]:not([data-cl-bound])'));
      if (!swatches.length) return;

      const container = swatches[0].parentNode;
      const ownIndex = swatches.findIndex((swatch) => this.ownHandle && swatch.dataset.handle === this.ownHandle);
      if (ownIndex > 0) {
        const [ownSwatch] = swatches.splice(ownIndex, 1);
        swatches.unshift(ownSwatch);
      }
      swatches.forEach((swatch) => container.appendChild(swatch));

      const MAX_VISIBLE_SWATCHES = 4;
      swatches.forEach((swatch, index) => {
        swatch.setAttribute('data-cl-bound', 'true');
        swatch.style.display = index >= MAX_VISIBLE_SWATCHES ? 'none' : '';

        if (this.ownHandle && swatch.dataset.handle === this.ownHandle) {
          swatch.classList.add('is-active');
          this.activeSwatch = swatch;
        }
      });

      if (container && !container.querySelector('[data-cl-swatch-more]')) {
        const remaining = swatches.length - MAX_VISIBLE_SWATCHES;
        if (remaining > 0) {
          const more = document.createElement('span');
          more.setAttribute('data-cl-swatch-more', '');
          more.className = 'sp-text-[15px] sp-text-[var(--text-light)] sp-ml-[0] sp-shrink-0 -sp-mt-[4px]';
          more.textContent = `+${remaining}`;
          container.appendChild(more);
        }
      }
    }

    commitSwatch(swatch) {
      if (this.activeSwatch) this.activeSwatch.classList.remove('is-active');
      this.activeSwatch = swatch;
      swatch.classList.add('is-active');
      this.applyState(swatch.dataset);
    }

    applyState(data) {
      if (this.mediaLink && data.url) this.mediaLink.setAttribute('href', data.url);
      if (this.titleLink && data.url) this.titleLink.setAttribute('href', data.url);
      if (this.titleLink && data.title) this.titleLink.textContent = data.title;
      if (this.priceElement && data.price) this.priceElement.textContent = data.price;
      if (data.images) {
        const leadAsset = this.pickLeadAsset(data.leadAssets);
        this.setGalleryImages(data.images.split('|').filter(Boolean), {
          leadAssetUrl: leadAsset && leadAsset.url,
          leadAssetIsVideo: !!(leadAsset && leadAsset.isVideo),
        });
      }
      this.applyBadge(this.pickBadgeCandidate(data.badgeCandidates));
    }

    _pickByCollection(candidates) {
      if (!Array.isArray(candidates) || !candidates.length) return null;

      const match = window.location.pathname.match(/^\/collections\/([^/]+)/);
      const currentHandle = match ? match[1] : '';
      const normalize = (value) => value.toLowerCase().trim().replace(/\s+/g, '-');

      return (
        candidates.find((c) => c.collection && normalize(c.collection) === currentHandle) ||
        candidates.find((c) => !c.collection) ||
        null
      );
    }

    pickLeadAsset(leadAssetsJSON) {
      const candidates = this._parseJSON(leadAssetsJSON, 'data-lead-assets');
      return candidates ? this._pickByCollection(candidates) : null;
    }

    pickBadgeCandidate(badgeCandidatesJSON) {
      const candidates = this._parseJSON(badgeCandidatesJSON, 'data-badge-candidates');
      return candidates ? this._pickByCollection(candidates) : null;
    }

    _parseJSON(json, attrNameForLogging) {
      if (!json) return null;
      try {
        return JSON.parse(json);
      } catch (error) {
        if (attrNameForLogging) {
          console.error(`[product-card] failed to JSON.parse ${attrNameForLogging}`, json, error);
        }
        return null;
      }
    }

    applyBadge(candidate) {
      if (!this.badgeContainer) return;
      this.badgeContainer.textContent = candidate ? candidate.text || '' : '';
    }

    setGalleryImages(urls, { leadAssetUrl, leadAssetIsVideo } = {}) {
      if (!urls || !urls.length) return;

      const template = this.images[0];
      if (!template) return;

      const templateSlide = template.closest('[data-product-card-slide]');
      if (!templateSlide) return;

      const container = templateSlide.parentNode;
      const lastSlide = this.images[this.images.length - 1].closest('[data-product-card-slide]');
      const referenceNode = lastSlide ? lastSlide.nextSibling : null;
      const imgClassName = template.className;
      const slideClassName = templateSlide.className;
      const overlayTemplate = templateSlide.querySelector('img[aria-hidden]');
      const overlaySrc = overlayTemplate ? overlayTemplate.getAttribute('src') : '';
      const overlayClassName = overlayTemplate ? overlayTemplate.className : '';

      const oldSlides = new Set(this.images.map((img) => img.closest('[data-product-card-slide]')).filter(Boolean));

      const leadingSlide = container.firstElementChild;
      if (leadingSlide && leadingSlide !== templateSlide && !oldSlides.has(leadingSlide)) {
        leadingSlide.remove();
      }

      oldSlides.forEach((slide) => slide.remove());

      const newImages = [];

      if (leadAssetUrl) {
        const leadSlide = document.createElement('div');
        leadSlide.setAttribute('data-product-card-slide', '');
        leadSlide.className = slideClassName;

        if (leadAssetIsVideo) {
          const video = document.createElement('video');
          video.className = imgClassName;
          video.classList.add('sp-opacity-100');
          video.classList.remove('sp-opacity-0');
          video.src = leadAssetUrl;
          video.autoplay = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          leadSlide.appendChild(video);
        } else {
          const leadImg = document.createElement('img');
          leadImg.className = imgClassName;
          leadImg.classList.add('sp-opacity-100');
          leadImg.classList.remove('sp-opacity-0');
          leadImg.src = leadAssetUrl;
          if (leadAssetUrl.indexOf('cdn.shopify.com') !== -1) {
            // Serve a size-matched file; the raw original moirés when downscaled.
            const join = leadAssetUrl.indexOf('?') === -1 ? '?' : '&';
            leadImg.src = `${leadAssetUrl}${join}width=900`;
            leadImg.srcset = [300, 450, 600, 750, 900, 1200, 1600]
              .map((width) => `${leadAssetUrl}${join}width=${width} ${width}w`)
              .join(', ');
            leadImg.sizes = '(min-width: 750px) 25vw, 50vw';
          }
          leadImg.alt = '';
          leadSlide.appendChild(leadImg);
          newImages.push(leadImg);
        }

        container.insertBefore(leadSlide, referenceNode);
      }

      urls.forEach((url, index) => {
        const slide = document.createElement('div');
        slide.setAttribute('data-product-card-slide', '');
        slide.className = slideClassName;

        const img = document.createElement('img');
        img.className = imgClassName;
        const isFirstVisible = index === 0 && !leadAssetUrl;
        img.src = url;
        img.classList.toggle('sp-opacity-100', isFirstVisible);
        img.classList.toggle('sp-opacity-0', !isFirstVisible);
        slide.appendChild(img);

        if (overlaySrc) {
          const overlayImg = document.createElement('img');
          overlayImg.className = overlayClassName;
          overlayImg.src = overlaySrc;
          overlayImg.alt = '';
          overlayImg.setAttribute('aria-hidden', 'true');
          slide.appendChild(overlayImg);
        }

        container.insertBefore(slide, referenceNode);
        newImages.push(img);
      });

      this.images = newImages;

      const totalSlides = urls.length + (leadAssetUrl ? 1 : 0);
      if (this.paginationInner) {
        this.paginationInner.style.width = `${(1 / totalSlides) * 100}%`;
      }
      if (this.mediaElement) {
        this.mediaElement.classList.toggle('has-single-image', totalSlides <= 1);
      }
    }
  }

  customElements.define('product-card', ProductCard);
}
